import clsx from 'clsx';
import {
  type FC,
  type KeyboardEvent,
  type PointerEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import {
  getEffectivePanelWidth,
  getMaxPanelWidth,
  MIN_PANEL_WIDTH,
  useSidePanelStore,
} from '@/components/side-panel/useSidePanelStore';

/** Width change (px) per arrow-key press for keyboard resizing. */
const KEYBOARD_STEP = 16;

/**
 * Draggable handle on the left edge of the side panel. Dragging left widens the
 * panel, dragging right narrows it. Width changes are pushed to the side-panel
 * store (which clamps and persists them). Also keyboard-accessible via arrows.
 */
export const PanelResizeHandle: FC = () => {
  const panelWidth = useSidePanelStore((state) => state.panelWidth);
  const isWidePanel = useSidePanelStore((state) => state.isWidePanel);
  const isResizing = useSidePanelStore((state) => state.isResizing);
  const setPanelWidth = useSidePanelStore((state) => state.setPanelWidth);
  const setResizing = useSidePanelStore((state) => state.setResizing);

  const dragRef = useRef({ startX: 0, startWidth: 0 });

  const currentWidth = getEffectivePanelWidth(panelWidth, isWidePanel);

  const handlePointerMove = useCallback(
    (event: globalThis.PointerEvent) => {
      // Panel sits on the right, so moving left (smaller clientX) widens it.
      const delta = dragRef.current.startX - event.clientX;
      setPanelWidth(dragRef.current.startWidth + delta);
    },
    [setPanelWidth]
  );

  const handlePointerUp = useCallback(() => {
    setResizing(false);
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', handlePointerUp);
    document.body.style.removeProperty('user-select');
  }, [handlePointerMove, setResizing]);

  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      dragRef.current = { startX: event.clientX, startWidth: currentWidth };
      setResizing(true);
      // Stop text selection flickering across the page mid-drag.
      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    },
    [currentWidth, handlePointerMove, handlePointerUp, setResizing]
  );

  // Defensive cleanup: if the panel unmounts mid-drag, drop the window
  // listeners so they can't keep resizing a panel that's gone.
  useEffect(
    () => () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      document.body.style.removeProperty('user-select');
    },
    [handlePointerMove, handlePointerUp]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setPanelWidth(currentWidth + KEYBOARD_STEP);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        setPanelWidth(currentWidth - KEYBOARD_STEP);
      }
    },
    [currentWidth, setPanelWidth]
  );

  return (
    <div
      role={'separator'}
      aria-orientation={'vertical'}
      aria-label={'Resize panel'}
      aria-valuenow={currentWidth}
      aria-valuemin={MIN_PANEL_WIDTH}
      aria-valuemax={getMaxPanelWidth()}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onKeyDown={handleKeyDown}
      className={clsx(
        'absolute left-0 top-0 z-10 h-full w-[6px] cursor-col-resize',
        'transition-colors hover:bg-grey300 focus-visible:bg-grey300 focus-visible:outline-none',
        isResizing && 'bg-grey400'
      )}
    />
  );
};
